import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { useLatestRef } from '@hooks/useLatestRef';
import { useReportNotificationClick } from '@hooks/useReportNotificationClick';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { resolveNotificationTarget } from '@lib/notifications/resolveNotificationTarget';
import { extractRefs } from '@lib/pwa/push-navigation';
import { SW_MESSAGE, type NotificationNavigateMessage } from '@lib/pwa/sw-messages';
import { isSafeInAppPath, NOTIFICATION_TAP_FALLBACK } from '@utils/safePath';
import { logger } from '@utils/logger';

/**
 * Relays NOTIFICATION_NAVIGATE messages from the service worker's
 * `notificationclick` handler to react-router navigation.
 *
 * N1 destination contract (task W3), dual-read transition:
 * - messages carrying `refs` with a `type` resolve through
 *   resolveNotificationTarget (the ONE destination authority); a known=false
 *   resolution fires logger.error plus notification_unknown_type with surface
 *   'sw_relay' (caller-owned side effects per the resolver contract) and then
 *   FALLS THROUGH to the legacy `url` the SW ships alongside refs, so a
 *   still-emitted legacy deep link is not thrown away during the transition;
 * - messages without refs (or with a type-less refs object - same guard as the
 *   native tap) keep the original url path through the shared isSafeInAppPath
 *   gate, unchanged.
 *
 * Extracted from WebPushInitializer so both the legacy and OneSignal
 * initializers can mount it. Note this is push-transport-agnostic: it listens
 * for the SW message regardless of which push pipeline delivered the
 * notification, so the OneSignal owner path must keep it mounted (legacy VAPID
 * pushes can still arrive during the migration overlap, before a device's
 * legacy subscription is torn down).
 *
 * `navigate` and `trackEvent` are captured through refs so the message
 * listener effect can run once (empty deps) without re-subscribing whenever
 * the router identity changes. The refs are synced via useEffect (not
 * assigned during render) to satisfy Rules of React's purity rule and the
 * React Compiler - mirrors usePushNotifications.ts / useEventStream.ts.
 */
export function useSwNotificationNavigate(): void {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitudeTracking();
  const reportClick = useReportNotificationClick();
  const navigateRef = useLatestRef(navigate);
  const trackEventRef = useLatestRef(trackEvent);
  const reportClickRef = useLatestRef(reportClick);

  // Handle NOTIFICATION_NAVIGATE messages from SW notificationclick
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      const data = event.data as Partial<NotificationNavigateMessage> | undefined;
      if (data?.type !== SW_MESSAGE.NOTIFICATION_NAVIGATE) return;

      // extractRefs (not sanitizeRefs) so a refs object with no `type` is
      // treated as absent and rides the legacy url path below - the SAME
      // type-requiring guard the native tap handler uses, so the two surfaces
      // cannot diverge on identical input.
      const refs = extractRefs(data.refs);
      if (refs) {
        // N3 "honest bell": report the tap up front (fire-and-forget) so the
        // click lands whether the destination resolves or falls through to the
        // legacy url. Guard the id-absent case (legacy / pre-deploy pushes).
        const notificationId = refs.notification_id;
        if (notificationId) reportClickRef.current(notificationId);
        const target = resolveNotificationTarget(refs.type, refs);
        if (target.known) {
          // Refs WIN over the legacy url when the contract resolves.
          navigateRef.current(target.path);
          return;
        }
        // Resolver abstained: fire telemetry ONCE, then FALL THROUGH to the
        // legacy url the SW ships alongside refs (known=false means "couldn't
        // decide", not "discard the legacy deep link"). The SW pre-gates url
        // through isSafeInAppPath, so this re-check is defensive; a
        // missing/unsafe url lands on the guest-safe home, never a dead-end.
        logger.error(
          `SW relay destination unresolved (type=${refs.type ?? 'missing'}); falling through to legacy url`,
        );
        trackEventRef.current(ANALYTICS_EVENTS.notification_unknown_type, {
          type: refs.type ?? null,
          surface: 'sw_relay',
        });
        navigateRef.current(isSafeInAppPath(data.url) ? data.url : NOTIFICATION_TAP_FALLBACK);
        return;
      }

      // Legacy path (no refs / type-less): SW already sanitized to a safe path;
      // re-check defensively with the shared gate. Unchanged - silent no-op on
      // an unsafe/missing url.
      if (isSafeInAppPath(data.url)) {
        navigateRef.current(data.url);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
    // navigateRef/trackEventRef/reportClickRef are stable useLatestRef handles -
    // listed to satisfy exhaustive-deps; the listener still registers exactly once.
  }, [navigateRef, trackEventRef, reportClickRef]);
}
